// STRIKZA — SLIP REPAIR · stub data (port slice)
//
// Throwaway fixture data so the page renders end-to-end on `npm run dev`
// without any backend. Translated from
// `Every Document/Gaps/Slip Repairer/handoff_slip_repair/slip-repair-data.jsx`
// into typed TS literals. Backend wiring is a future slice; per the
// build brief, action handlers stub to console.log with TODO comments.
//
// Redaction notes (STR-PROD-008 v2.1.1, applied at port time):
//   • R3 — `decision: 'contested'` renamed to `'unresolved'`;
//          `contestedAgents` replaced with `unresolvedReason`
//          ({ covered, missing }) for the repurposed split-view.
//   • Agent ids stay in props but are NOT rendered (R1).
//   • Win-probability factor `id: 'wp'` renamed `'div'` (Diversification)
//          per v2.1.1 §4.4.

export type DecisionType = "retain" | "replace" | "flag" | "unresolved";
export type VoidLevel = "HIGH" | "MEDIUM" | "LOW";
export type RepairConfidence = "HIGH" | "MEDIUM" | "LOW";
// SR-TIERS (2026-05-28): 4-tier model replaces the old Sharp/Safer/Both
// posture at the user-facing layer. RepairTier is the new toggle. The
// legacy RepairMode = "sharp" | "safer" stays as the backend-mirror type
// (patchLeg.user_mode_chosen, exportPdf.mode, the legacy sharp/safer DB
// columns). The UI maps tier → mode for legacy calls: bold/moderate →
// "sharp" (the dream-protective side), safer/conservative → "safer".
export type RepairTier = "bold" | "moderate" | "safer" | "conservative";
export type RepairMode = "sharp" | "safer";
export type RunMode = RepairTier;

export function tierToLegacyMode(t: RepairTier): RepairMode {
  return t === "bold" || t === "moderate" ? "sharp" : "safer";
}
export type EntryMode = "pre-placement" | "post-build";
export type HealthBand = "strong" | "good" | "fair" | "needs-work";

export type ContextSignalKey =
  | "default"
  | "lm3"
  | "lm2"
  | "dr1"
  | "rest"
  | "ref"
  | "rotation"
  | "derby";

export interface HealthFactor {
  // 'div' = Diversification (renamed from 'wp' per v2.1.1 §4.4).
  // 'void' factor removed (SR-VOID-DEFRAME) — kept in the union for
  // backward compat with persisted stub data, but no longer rendered.
  id: "void" | "mkt" | "clv" | "div";
  label: string;
  value: number;
  weight: number;
  desc: string;
}

export interface SlipHealthData {
  score: number;
  band: "STRONG" | "GOOD" | "FAIR" | "NEEDS WORK";
  factors: HealthFactor[];
  scoreAfterSharp: number;
  bandAfterSharp: string;
  scoreAfterSafer: number;
  bandAfterSafer: string;
}

export interface VoidExposure {
  originalHigh: number;
  originalMedium: number;
  afterRepair: number;
}

export interface SlipMeta {
  bookmaker: string;
  bookmakerShort: string;
  bookmakerColor: string;
  slipCode: string;
  uploaded: string;
  uploadedDate: string;
  legCount: number;
  stake: number;
  currency: string;
  origOdds: number;
  origPayout: number;
  origWinProb: number;
  sharpOdds: number;
  sharpPayout: number;
  sharpWinProb: number;
  saferOdds: number;
  saferPayout: number;
  saferWinProb: number;
  repairedAt: string;
  repairedDate: string;
  validUntilMin: number;
  health: SlipHealthData;
  voidExposure: VoidExposure;
  marketsScanned: number;
  fixturesAnalysed: number;
  agentPairsDispatched: number;
  // v2.1.1 — LLM-generated 2–3 sentence executive summary above the
  // summary bar. Stubbed here.
  executiveSummary: string;
}

export interface RepairPickSharp {
  market: string;
  selection: string;
  odds: number;
  clvDelta: number;
  winProb: number;
}
export interface RepairPickSafer {
  market: string;
  selection: string;
  odds: number;
  probDelta: number;
  winProb: number;
}
export interface RepairPickPair {
  sharp: RepairPickSharp;
  safer: RepairPickSafer;
}

export interface UnresolvedReason {
  // Repurposed split-view (R3) — what we DO have coverage for on this
  // fixture this week, vs what we DON'T. Same UI components as the
  // old CONTESTED split, different semantics.
  covered: string;
  missing: string;
}

export interface OriginalPick {
  market: string;
  selection: string;
  odds: number;
  voidRisk: VoidLevel;
  voidCondition?: string;
  excludedMarket?: true;
}

// SR-TIERS — per-tier pick the LegCard renders when a tier is selected.
export interface TierPick {
  market: string;
  selection: string;
  odds: number;
  decision: "REPLACE" | "RETAIN" | "FLAG" | "UNRESOLVED" | "MARGINAL";
  reason: string | null;
}
export type TierPicks = Partial<Record<RepairTier, TierPick>>;

export interface Leg {
  id: string;
  n: number;
  fixture: { home: string; away: string };
  league: string;
  kickoff: string;
  decision: DecisionType;
  confidence: number;
  contextSignal: ContextSignalKey;
  // Agent ids retained in DATA per R1 (admin diagnostics future); the
  // user-facing card never renders them.
  agents: { primary: string; secondary: string };
  repairConfidence: RepairConfidence;
  original: OriginalPick;
  repair: RepairPickPair | null;
  // SR-TIERS — per-tier picks from slip_repair_legs.tier_picks. Optional
  // for backwards-compat with the stub fixture data (which still uses
  // the legacy `repair` field).
  tierPicks?: TierPicks;
  unresolvedReason?: UnresolvedReason;
  reasoning: { headline: string; body: string };
  altMode: "safer-stronger" | "sharp-different" | null;
}

export interface ExcludedMarket {
  name: string;
  reason: string;
}

export interface ContextSignalDef {
  label: string;
  tone: "neu" | "sharp" | "safer" | "warn" | "flag";
}

export const CONTEXT_SIGNALS: Record<ContextSignalKey, ContextSignalDef> = {
  default: { label: "Default · balanced fixture", tone: "neu" },
  lm3: { label: "Strong Line Movement · LM-3", tone: "sharp" },
  lm2: { label: "Strong Line Movement · LM-2", tone: "sharp" },
  dr1: { label: "Dead Rubber · DR-1", tone: "flag" },
  rest: { label: "Rest Differential · BAND-3", tone: "safer" },
  ref: { label: "High-Profile Referee", tone: "warn" },
  rotation: { label: "Squad Congestion · rotation signal", tone: "safer" },
  derby: { label: "Derby · elevated volatility", tone: "warn" },
};

export const EXCLUDED_MARKETS: ExcludedMarket[] = [
  {
    name: "Correct Score",
    reason:
      "Severe bookmaker margin (~25%) with no systematic edge. Win rate is structurally below threshold across every league we have tracked.",
  },
  {
    name: "First Goalscorer",
    reason:
      "High variance with no model-derivable signal. Margin compounds across the goalscorer list — pure trap market.",
  },
  {
    name: "Anytime Goalscorer",
    reason:
      "Public-facing market priced at consensus. Margin too steep for sharp money to land here.",
  },
  {
    name: "Scorecast / Wincast",
    reason:
      "Combination of two trap markets. Layered margin destroys any edge.",
  },
  {
    name: "Exact Goals",
    reason:
      "Three or more discrete outcomes priced against the bettor. No agent pair can clear the threshold.",
  },
  {
    name: "Half-Time Correct Score",
    reason:
      "Even higher margin than full-time Correct Score. Same structural problem.",
  },
  {
    name: "Minute of First Goal",
    reason:
      "Pure variance. No bookmaker model is sharp here. No catalogue tier survived backtest.",
  },
];

export const SLIP_META: SlipMeta = {
  bookmaker: "SportyBet",
  bookmakerShort: "SB",
  bookmakerColor: "#d62828",
  slipCode: "B7K9-23M1-XF",
  uploaded: "16:42",
  uploadedDate: "Sat 18 May",
  legCount: 7,
  stake: 50.0,
  currency: "GH₵",
  origOdds: 343.45,
  origPayout: 17172.5,
  origWinProb: 0.42,
  sharpOdds: 21.84,
  sharpPayout: 1092.0,
  sharpWinProb: 3.84,
  saferOdds: 11.62,
  saferPayout: 581.0,
  saferWinProb: 7.92,
  repairedAt: "16:42",
  repairedDate: "18 May 2026",
  validUntilMin: 107,
  health: {
    score: 42,
    band: "FAIR",
    factors: [
      {
        id: "mkt",
        label: "Market quality",
        value: 57,
        weight: 45,
        desc: "1 leg in an excluded market (Correct Score). 1 leg in a low-edge catalogue tier.",
      },
      {
        id: "clv",
        label: "CLV alignment",
        value: 32,
        weight: 30,
        desc: "4 of 7 legs sit on the soft side of the Pinnacle opening line.",
      },
      {
        id: "div",
        label: "Diversification",
        value: 49,
        weight: 25,
        desc: "Legs concentrated in 3 market families — Total Goals and Asian Handicap dominate. Spread across more families to reduce correlated downside.",
      },
    ],
    scoreAfterSharp: 79,
    bandAfterSharp: "GOOD",
    scoreAfterSafer: 86,
    bandAfterSafer: "STRONG",
  },
  voidExposure: {
    originalHigh: 4,
    originalMedium: 1,
    afterRepair: 0,
  },
  marketsScanned: 2847,
  fixturesAnalysed: 7,
  agentPairsDispatched: 7,
  executiveSummary:
    "Your slip carries four HIGH-void legs and sits on the soft side of the Pinnacle line on most fixtures — repair pulls void exposure to zero and lifts landing probability from 0.42% to 3.84% in Sharp mode. One leg has unresolved coverage this week; you decide on that one.",
};

export const SLIP_LEGS: Leg[] = [
  {
    id: "l1",
    n: 1,
    fixture: { home: "Real Madrid", away: "Sevilla" },
    league: "La Liga",
    kickoff: "Sat 21:00",
    decision: "retain",
    confidence: 0.94,
    contextSignal: "default",
    agents: { primary: "A-18", secondary: "A-08" },
    repairConfidence: "HIGH",
    original: {
      market: "Total Goals · Over/Under",
      selection: "Over 2.5",
      odds: 1.72,
      voidRisk: "LOW",
    },
    repair: null,
    reasoning: {
      headline: "Strikza agrees with your pick.",
      body: "Low void risk and strong edge. The Pinnacle line opens at Over 2.5 and sharp money sits this side. No better alternative found on this fixture.",
    },
    altMode: null,
  },
  {
    id: "l2",
    n: 2,
    fixture: { home: "Liverpool", away: "Manchester City" },
    league: "Premier League",
    kickoff: "Sat 17:30",
    decision: "replace",
    confidence: 0.97,
    contextSignal: "lm3",
    agents: { primary: "A-08", secondary: "A-18" },
    repairConfidence: "HIGH",
    original: {
      market: "Draw No Bet",
      selection: "Liverpool",
      odds: 1.85,
      voidRisk: "HIGH",
      voidCondition: "Voids if match ends in a draw.",
    },
    repair: {
      sharp: {
        market: "Double Chance · 1X",
        selection: "Liverpool or Draw",
        odds: 1.42,
        clvDelta: 12,
        winProb: 71.4,
      },
      safer: {
        market: "Double Chance · 1X",
        selection: "Liverpool or Draw",
        odds: 1.42,
        probDelta: 18,
        winProb: 71.4,
      },
    },
    reasoning: {
      headline: "Your original carries HIGH void risk.",
      body: "Draw No Bet voids if the match ends in a draw — your odds disappear from the accumulator with no replacement. Double Chance covers two of three outcomes and cannot void.",
    },
    altMode: null,
  },
  {
    id: "l3",
    n: 3,
    fixture: { home: "Arsenal", away: "Tottenham" },
    league: "Premier League",
    kickoff: "Sat 15:00",
    decision: "replace",
    confidence: 0.91,
    contextSignal: "derby",
    agents: { primary: "A-18", secondary: "A-08" },
    repairConfidence: "MEDIUM",
    original: {
      market: "Asian Handicap",
      selection: "Arsenal -1.0",
      odds: 2.1,
      voidRisk: "HIGH",
      voidCondition: "Voids if Arsenal win by exactly 1 goal.",
    },
    repair: {
      sharp: {
        market: "Match Result · 1X2",
        selection: "Arsenal to Win",
        odds: 1.62,
        clvDelta: 8,
        winProb: 61.7,
      },
      safer: {
        market: "Double Chance · 1X",
        selection: "Arsenal or Draw",
        odds: 1.28,
        probDelta: 21,
        winProb: 78.1,
      },
    },
    reasoning: {
      headline: "AH -1.0 voids on a 1-goal win — replacement removes that risk.",
      body: "Asian Handicap at the exact line refunds your stake if Arsenal win by exactly one goal. The replacement market cleanly settles in both win and draw scenarios.",
    },
    altMode: "safer-stronger",
  },
  {
    // R3 — was 'contested'; now 'unresolved'. Split-view repurposed:
    // "covered" vs "missing" semantics instead of agent-vs-agent.
    id: "l4",
    n: 4,
    fixture: { home: "Bayern München", away: "Borussia Dortmund" },
    league: "Bundesliga",
    kickoff: "Sat 18:30",
    decision: "unresolved",
    confidence: 0.93,
    contextSignal: "derby",
    agents: { primary: "A-03", secondary: "A-18" },
    repairConfidence: "LOW",
    original: {
      market: "Both Teams to Score",
      selection: "Yes",
      odds: 1.5,
      voidRisk: "LOW",
    },
    repair: {
      sharp: {
        market: "Total Goals · Over/Under",
        selection: "Over 2.5",
        odds: 1.65,
        clvDelta: 6,
        winProb: 60.6,
      },
      safer: {
        market: "Total Goals · Over/Under",
        selection: "Over 2.5",
        odds: 1.65,
        probDelta: 9,
        winProb: 60.6,
      },
    },
    unresolvedReason: {
      covered:
        "Total Goals · Over/Under — full substrate coverage on this fixture this week. Fair price written, Pinnacle close referenced.",
      missing:
        "Both Teams to Score — no qualifying BTTS price in the substrate for this fixture this week. We do not synthesise a BTTS verdict from goal-totals; the markets are not equivalent.",
    },
    reasoning: {
      headline: "We have coverage on Total Goals but not on BTTS for this fixture.",
      body: "The substrate priced Total Goals · Over 2.5 with full confidence this week, but did not surface a BTTS verdict on Bayern/Dortmund. Your judgement on this fixture wins — accept the Total Goals swap, keep BTTS, or remove the leg.",
    },
    altMode: null,
  },
  {
    id: "l5",
    n: 5,
    fixture: { home: "Hearts of Oak", away: "Asante Kotoko" },
    league: "Ghana Premier League",
    kickoff: "Sun 16:00",
    decision: "replace",
    confidence: 0.88,
    contextSignal: "rotation",
    agents: { primary: "A-09", secondary: "A-14" },
    repairConfidence: "HIGH",
    original: {
      market: "Correct Score",
      selection: "2-1 Hearts of Oak",
      odds: 9.5,
      voidRisk: "MEDIUM",
      excludedMarket: true,
    },
    repair: {
      sharp: {
        market: "Match Result · 1X2",
        selection: "Hearts of Oak to Win",
        odds: 2.4,
        clvDelta: 5,
        winProb: 42.1,
      },
      safer: {
        market: "Double Chance · 1X",
        selection: "Hearts of Oak or Draw",
        odds: 1.55,
        probDelta: 38,
        winProb: 66.1,
      },
    },
    reasoning: {
      headline: "Correct Score is on our exclusion list — bookmaker trap market.",
      body: "We never suggest Correct Score replacements. The margin is too steep (~25%) and no agent pair has cleared threshold on this market in any league we track.",
    },
    altMode: "sharp-different",
  },
  {
    id: "l6",
    n: 6,
    fixture: { home: "Paris Saint-Germain", away: "Lyon" },
    league: "Ligue 1",
    kickoff: "Sun 20:45",
    decision: "flag",
    confidence: 0.92,
    contextSignal: "ref",
    agents: { primary: "A-05", secondary: "A-03" },
    repairConfidence: "MEDIUM",
    original: {
      market: "Total Goals · Over/Under",
      selection: "Over 4.5",
      odds: 5.5,
      voidRisk: "LOW",
    },
    repair: null,
    reasoning: {
      headline: "No qualifying replacement found at this time.",
      body: "Over 4.5 Goals is structurally low-probability and on PSG/Lyon the Pinnacle line agrees. No same-fixture market in the premium catalogue clears the minimum improvement threshold in either Sharp or Safer mode.",
    },
    altMode: null,
  },
  {
    id: "l7",
    n: 7,
    fixture: { home: "Chelsea", away: "Aston Villa" },
    league: "Premier League",
    kickoff: "Sun 14:00",
    decision: "replace",
    confidence: 0.95,
    contextSignal: "default",
    agents: { primary: "A-18", secondary: "A-08" },
    repairConfidence: "HIGH",
    original: {
      market: "Asian Handicap",
      selection: "Chelsea -0.5,-1",
      odds: 1.95,
      voidRisk: "HIGH",
      voidCondition: "Half-stake refunds if Chelsea win by exactly 1 goal.",
    },
    repair: {
      sharp: {
        market: "Match Result · 1X2",
        selection: "Chelsea to Win",
        odds: 1.85,
        clvDelta: 6,
        winProb: 54.1,
      },
      safer: {
        market: "Double Chance · 1X",
        selection: "Chelsea or Draw",
        odds: 1.32,
        probDelta: 22,
        winProb: 75.8,
      },
    },
    reasoning: {
      headline: "Split AH refunds half your stake on a 1-goal Chelsea win.",
      body: "Quarter-line Asian Handicaps split the stake — a 1-goal Chelsea win refunds 50% and settles the rest as a loss. Moneyline cleanly settles.",
    },
    altMode: "safer-stronger",
  },
];
